import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowDownLeft, ArrowUpRight, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useTicketEmails } from '@/queries/emailLog'
import type { EmailLogEntry } from '@/types/emailLog'
import { counterparty, emailStatusLabels, emailStatusVariants, kindLabel, openedByCount, readSummary, statusBreakdown } from './emailStatus'
import { EmailDetailDialog } from './EmailDetailDialog'

function EmailLine({ entry, onOpen }: { entry: EmailLogEntry; onOpen: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = entry.direction === 'outbound' ? ArrowUpRight : ArrowDownLeft
  const many = entry.recipientCount > 1
  const breakdown = statusBreakdown(entry)
  const readers = entry.direction === 'outbound' ? openedByCount(entry) : 0

  return (
    <li data-testid="ticket-email">
      <div className="flex items-center gap-2 rounded-etn-menu px-1 py-1 text-etn-micro hover:bg-etn-surface">
        <Icon className="h-3.5 w-3.5 shrink-0 text-etn-text-soft" aria-label={entry.direction === 'outbound' ? 'Outbound' : 'Inbound'} />
        <button type="button" onClick={() => onOpen(entry._id)} className="min-w-0 flex-1 text-left">
          <span className="block truncate font-medium text-etn-text-cell">{kindLabel(entry)}</span>
          <span className="block truncate text-etn-text-soft">
            {counterparty(entry)}, {formatDateTime(entry.createdAt)}
          </span>
          {readers > 0 && <span className="block text-etn-success-text">Opened by {readers}</span>}
        </button>
        <Badge variant={emailStatusVariants[entry.status]} title={breakdown ?? undefined}>
          {emailStatusLabels[entry.status]}
        </Badge>
        {many && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide recipients' : 'Show recipients'}
            onClick={() => setExpanded(v => !v)}
            className="rounded-etn-menu p-0.5 text-etn-text-soft hover:text-etn-text"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
      </div>
      {many && expanded && (
        <ul className="mb-1 ml-6 space-y-1 border-l border-etn-border-hairline pl-2.5" aria-label="Recipients">
          {entry.recipients.map(r => (
            <li key={r._id} className="flex items-center gap-2 text-etn-micro">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-etn-text-cell">{r.address}</span>
                {readSummary(r) && <span className="block text-etn-text-soft">{readSummary(r)}</span>}
              </span>
              <Badge variant={emailStatusVariants[r.status]}>{emailStatusLabels[r.status]}</Badge>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

// Body of the staff rail's "Emails" disclosure: one line per email (a pool
// notification is one line, expandable to its recipients). Fetches only once
// the disclosure has been opened: most visits to a ticket never look.
export function TicketEmails({ ticketId, enabled }: { ticketId: string; enabled: boolean }) {
  const { data, isLoading, error } = useTicketEmails(ticketId, enabled)
  const [openId, setOpenId] = useState<string | undefined>()

  if (isLoading) return <p className="text-etn-micro text-etn-text-soft">Loading…</p>
  if (error) return <p className="text-etn-micro text-etn-danger-text">Couldn't load emails.</p>
  const rows = data?.data ?? []
  if (!rows.length) return <p className="text-etn-micro text-etn-text-soft">No emails for this ticket yet.</p>

  return (
    <div className="space-y-2">
      <ul className="space-y-1">
        {rows.map(entry => (
          <EmailLine key={entry._id} entry={entry} onOpen={setOpenId} />
        ))}
      </ul>
      <Link
        to={'/admin/email-log' as never}
        search={{ ticketId } as never}
        className="inline-block text-etn-micro font-semibold text-etn-accent-text-active hover:underline"
      >
        Open in email log
      </Link>
      <EmailDetailDialog id={openId} onClose={() => setOpenId(undefined)} />
    </div>
  )
}
